import {useMemo} from 'react'
import {useSelector} from 'react-redux'
import {getLocaleCode} from 'modules/meta/selectors'
import {getHotelResult} from 'modules/searchApi/selectors'
import {useGetBreadcrumbQuery} from 'modules/seoApi'
import {getBreadcrumbParams} from 'modules/seoApi/selectors'
import {
  ApSeoBreadcrumb,
  getApSeoBreadcrumb
} from 'utils/server/apSeo/getApSeoBreadcrumb'

import {useHotelSlug} from './useHotelSlug'
import {useIsSrpSearchSeoFlow} from './useIsSrpSearchSeoFlow'

/**
 * Retrieves the correct SEO breadcrumb for the AP when the it's the SEO search flow.
 */
export const useApSeoBreadcrumb = () => {
  const hotelSlug = useHotelSlug()
  const {data: hotel} = useSelector(state => getHotelResult(state, hotelSlug))

  const locale = useSelector(getLocaleCode)
  const isSrpSearchSeoFlow = useIsSrpSearchSeoFlow()
  const params = useSelector(state =>
    getBreadcrumbParams(state, hotel?.placeID)
  )
  const {breadcrumb} = useGetBreadcrumbQuery(params, {
    selectFromResult: result => ({breadcrumb: result?.data?.breadcrumb}),
    skip: !isSrpSearchSeoFlow
  })

  return useMemo(() => {
    if (!locale || !hotel || !isSrpSearchSeoFlow) {
      return []
    }

    return getApSeoBreadcrumb({locale, hotel, breadcrumb})
  }, [locale, hotel, isSrpSearchSeoFlow, breadcrumb])
}

/**
 * Returns the parent place from the SEO breadcrumb (the level above the hotel, e.g. city or region).
 * Returns undefined when not in a SEO flow or when the breadcrumb has fewer than 2 entries.
 */
export const useApSeoParentPlace = (): ApSeoBreadcrumb | undefined => {
  const seoBreadcrumb = useApSeoBreadcrumb()
  // Some places might not have a city, so as a safety measure, always get the penultimate position. The hotel name is always the last one.
  return seoBreadcrumb.at(-2)
}
